import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, ShieldCheck, IndianRupee, MessageSquare, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { MKT_CSS, MktHeader, ItemCard, FreelancerCard, SectionHead } from "./MktShared";
import { LoadingRows } from "../../components/Shared";

export default function MarketplaceHome() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/mkt/home").then((r) => setData(r.data)).catch(() => setData({ categories: [], services: [], products: [], courses: [], freelancers: [], popular: [] }));
  }, []);

  const search = (e) => { e.preventDefault(); navigate(`/marketplace/search?q=${encodeURIComponent(q)}`); };

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />

      {/* HERO */}
      <section className="mkt-hero relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,.35) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,.25) 0, transparent 40%)" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center text-white">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 mkt-glass rounded-full px-4 py-1.5 text-xs font-bold tracking-wide uppercase">
            <Sparkles className="h-3.5 w-3.5" /> MILTROO Business Marketplace
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.05]">
            Hire verified experts.<br className="hidden sm:block" /> Grow your business faster.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
            className="text-white/85 text-base sm:text-lg mt-4 max-w-2xl mx-auto">
            Services, digital products and courses from trusted MILTROO partners — with secure platform payments and buyer protection.
          </motion.p>
          <motion.form initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} onSubmit={search}
            className="mt-8 max-w-2xl mx-auto flex items-center gap-2 bg-white rounded-full p-2 shadow-2xl shadow-black/20">
            <Search className="h-5 w-5 text-slate-500 ml-3 shrink-0" />
            <input data-testid="mkt-hero-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder='Try "logo design" or "website development"'
              className="flex-1 h-11 bg-transparent text-slate-900 text-sm sm:text-base focus:outline-none min-w-0" />
            <button data-testid="mkt-hero-search-btn" type="submit"
              className="h-11 px-5 sm:px-7 rounded-full font-bold text-white text-sm shrink-0 active:scale-95 transition-transform"
              style={{ background: "linear-gradient(135deg, #007A4D, #2563EB)" }}>Search</button>
          </motion.form>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="mt-5 flex flex-wrap justify-center gap-2">
            {(data?.popular || []).slice(0, 6).map((p) => (
              <button key={p} data-testid={`mkt-popular-${p.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => navigate(`/marketplace/search?q=${encodeURIComponent(p)}`)}
                className="mkt-glass text-white/90 text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-white/20 transition-colors">{p}</button>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42 }}
            className="mt-7 flex flex-wrap justify-center items-center gap-3" data-testid="mkt-join-ctas">
            <Link to="/mkt/auth?mode=register" data-testid="mkt-register-free"
              className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold text-sm px-7 py-3.5 rounded-full hover:scale-105 active:scale-95 transition-transform shadow-xl">
              Register Free
            </Link>
            <Link to="/join-freelancer" data-testid="mkt-become-freelancer-hero"
              className="inline-flex items-center gap-2 mkt-glass text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-white/20 transition-colors">
              Become a Freelancer
            </Link>
            <span className="text-white/70 text-[11px] font-semibold w-full sm:w-auto">Buyers: FREE forever · Freelancers: small monthly membership</span>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-border/60 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: "Buyer Protection", d: "Payment released only after you approve delivery" },
            { icon: IndianRupee, t: "Transparent Pricing", d: "Fixed packages, no hidden charges" },
            { icon: MessageSquare, t: "Secure Chat & Offers", d: "Discuss requirements and get custom offers" },
          ].map((x, i) => (
            <motion.div key={x.t} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl mkt-grad-fallback flex items-center justify-center shrink-0"><x.icon className="h-5 w-5 text-white" /></div>
              <div><p className="font-bold text-sm">{x.t}</p><p className="text-xs text-muted-foreground mt-0.5">{x.d}</p></div>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-16">
        {!data && <LoadingRows rows={4} />}

        {/* CATEGORIES */}
        {data && data.categories.length > 0 && (
          <section data-testid="mkt-categories-section">
            <SectionHead title="Browse Categories" sub="Everything your business needs, in one place" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.categories.map((c, i) => (
                <motion.button key={c.id} data-testid={`mkt-category-${c.slug}`} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.03, 0.3) }}
                  onClick={() => navigate(`/marketplace/search?category=${encodeURIComponent(c.name)}`)}
                  className="mkt-card p-4 text-left font-bold text-sm hover:border-primary/40 flex items-center justify-between gap-2 group">
                  {c.name}
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all" />
                </motion.button>
              ))}
            </div>
          </section>
        )}

        {/* SERVICES */}
        {data && data.services.length > 0 && (
          <section data-testid="mkt-services-section">
            <SectionHead title="Featured Services" sub="Fixed-price packages from verified freelancers"
              action={<Link to="/marketplace/search?tab=services" data-testid="mkt-view-services" className="text-sm font-bold text-gradient inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.services.map((s, i) => <ItemCard key={s.id} item={s} kind="service" delay={i} />)}
            </div>
          </section>
        )}

        {/* PRODUCTS */}
        {data && data.products.length > 0 && (
          <section data-testid="mkt-products-section">
            <SectionHead title="Digital Products" sub="Templates, tools and assets — instant download"
              action={<Link to="/marketplace/search?tab=products" data-testid="mkt-view-products" className="text-sm font-bold text-gradient inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.products.map((p, i) => <ItemCard key={p.id} item={p} kind="product" delay={i} />)}
            </div>
          </section>
        )}

        {/* COURSES */}
        {data && data.courses.length > 0 && (
          <section data-testid="mkt-courses-section">
            <SectionHead title="Courses" sub="Learn directly from practitioners"
              action={<Link to="/marketplace/search?tab=courses" data-testid="mkt-view-courses" className="text-sm font-bold text-gradient inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.courses.map((c, i) => <ItemCard key={c.id} item={c} kind="course" delay={i} />)}
            </div>
          </section>
        )}

        {/* FREELANCERS */}
        {data && data.freelancers.length > 0 && (
          <section data-testid="mkt-freelancers-section">
            <SectionHead title="Top Freelancers" sub="Verified MILTROO partners ready to work" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {data.freelancers.map((f, i) => <FreelancerCard key={f.slug} f={f} delay={i} />)}
            </div>
          </section>
        )}

        {data && !data.services.length && !data.products.length && !data.courses.length && !data.freelancers.length && (
          <div data-testid="mkt-empty" className="text-center py-16 space-y-3">
            <p className="font-display font-extrabold text-2xl mkt-sec-title">The marketplace is launching soon</p>
            <p className="text-sm text-muted-foreground">Freelancers are setting up their stores. Check back shortly — or become one of the first sellers.</p>
          </div>
        )}

        {/* SELLER CTA */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mkt-hero rounded-3xl p-8 sm:p-12 text-white text-center relative overflow-hidden mkt-shine">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Earn with your skills on MILTROO</h2>
          <p className="text-white/85 mt-3 max-w-xl mx-auto text-sm sm:text-base">
            Already a MILTROO partner? Activate your freelancer profile in one click and start selling services, digital products and courses.
          </p>
          <Link to="/join-freelancer" data-testid="mkt-become-freelancer"
            className="inline-flex items-center gap-2 mt-6 bg-white text-slate-900 font-bold text-sm px-7 py-3.5 rounded-full hover:scale-105 active:scale-95 transition-transform shadow-xl">
            Join as Freelancer <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.section>
      </div>
    </div>
  );
}
